import {
  cargoTypeList,
  dataSourceTypeList,
  missingAisDataList,
  typeOfMobile,
} from './hardcodedValues';
import {
  PiAnchorSimpleThin,
  PiArrowClockwiseThin,
  PiArrowsLeftRightThin,
  PiArrowsOutLineHorizontalThin,
  PiBoatThin,
  PiCalendarThin,
  PiCircleThin,
  PiCodesandboxLogoThin,
  PiCompassThin,
  PiFlagThin,
  PiFunnelThin,
  PiGaugeThin,
  PiHashThin,
  PiIdentificationCardThin,
  PiMapPinLineThin,
  PiMapPinThin,
  PiPathThin,
  PiPolygonThin,
  PiRadioThin,
  PiTagThin,
} from 'react-icons/pi';
import { CiSatellite1 } from 'react-icons/ci';
import { IconType } from 'react-icons';

interface Range {
  min: number;
  max: number;
}

interface Limit {
  minChars?: number;
  maxChars?: number;
  range?: Range;
  isDate?: boolean;
  hasMultiple?: boolean;
}

export enum Operator {
  Equal = '=',
  NotEqual = '!=',
  GreaterThan = '>',
  LessThan = '<',
  Contains = '=', // is the same as equal because it's the wildcard option (*) that is used alongside
  NotContains = '!=', // is the same as not equal because it's the wildcard option (*) that is used alongside
  StartsWith = '=',
  DoesNotStartWith = '!=',
  EndsWith = '=',
  DoesNotEndWith = '!=',
  GreaterThanOrEqual = '>=',
  LessThanOrEqual = '<=',
  InsideArea = 'INSIDE_AREA',
  OutsideArea = 'OUTSIDE_AREA',
  InsideCircle = 'INSIDE',
  OutsideCircle = 'OUTSIDE',
  Boolean = 'BOOL',
}

export enum HumanOperator {
  Equal = 'equal to',
  NotEqual = 'not equal to',
  GreaterThan = 'greater than',
  LessThan = 'less than',
  Contains = 'contains',
  NotContains = 'does not contain',
  StartsWith = 'starts with',
  DoesNotStartWith = 'does not start with',
  EndsWith = 'ends with',
  DoesNotEndWith = 'does not end with',
  GreaterThanOrEqual = 'greater than or equal',
  LessThanOrEqual = 'less than or equal',
  InsideArea = 'inside polygon',
  OutsideArea = 'outside polygon',
  InsideCircle = 'inside circle',
  OutsideCircle = 'outside circle',
  Boolean = 'boolean',
}

export const humanOperatorToOperator = (
  humanOperator: HumanOperator
): Operator => {
  switch (humanOperator) {
    case HumanOperator.Equal:
      return Operator.Equal;
    case HumanOperator.NotEqual:
      return Operator.NotEqual;
    case HumanOperator.GreaterThan:
      return Operator.GreaterThan;
    case HumanOperator.LessThan:
      return Operator.LessThan;
    case HumanOperator.Contains:
      return Operator.Contains;
    case HumanOperator.NotContains:
      return Operator.NotContains;
    case HumanOperator.StartsWith:
      return Operator.StartsWith;
    case HumanOperator.DoesNotStartWith:
      return Operator.DoesNotStartWith;
    case HumanOperator.EndsWith:
      return Operator.EndsWith;
    case HumanOperator.DoesNotEndWith:
      return Operator.DoesNotEndWith;
    case HumanOperator.GreaterThanOrEqual:
      return Operator.GreaterThanOrEqual;
    case HumanOperator.LessThanOrEqual:
      return Operator.LessThanOrEqual;
    case HumanOperator.InsideArea:
      return Operator.InsideArea;
    case HumanOperator.OutsideArea:
      return Operator.OutsideArea;
    case HumanOperator.InsideCircle:
      return Operator.InsideCircle;
    case HumanOperator.OutsideCircle:
      return Operator.OutsideCircle;
    case HumanOperator.Boolean:
      return Operator.Boolean;
    default:
      throw new Error('Invalid HumanOperator');
  }
};

export const operatorAndValueToHumanOperator = (
  operator: string,
  value: string | number | boolean
): HumanOperator => {
  const trimQuotes = (value: string) =>
    value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1) : value;
  const containsWildcard = (value: string | number | boolean) => {
    if (typeof value !== 'string') return false;
    return value.startsWith('*') && value.endsWith('*');
  };

  const startsWithWildcard = (value: string | number | boolean) => {
    if (typeof value !== 'string') return false;
    return value.startsWith('*') && !value.endsWith('*');
  };

  const endsWithWildcard = (value: string | number | boolean) => {
    if (typeof value !== 'string') return false;
    return !value.startsWith('*') && value.endsWith('*');
  };

  const trimmedValue = typeof value === 'string' ? trimQuotes(value) : value;

  switch (operator) {
    case Operator.Equal:
      if (containsWildcard(trimmedValue)) return HumanOperator.Contains;
      if (startsWithWildcard(trimmedValue)) return HumanOperator.EndsWith;
      if (endsWithWildcard(trimmedValue)) return HumanOperator.StartsWith;
      return HumanOperator.Equal;
    case Operator.NotEqual:
      if (containsWildcard(trimmedValue)) return HumanOperator.NotContains;
      if (startsWithWildcard(trimmedValue)) return HumanOperator.DoesNotEndWith;
      if (endsWithWildcard(trimmedValue)) return HumanOperator.DoesNotStartWith;
      return HumanOperator.NotEqual;
    case Operator.GreaterThan:
      return HumanOperator.GreaterThan;
    case Operator.LessThan:
      return HumanOperator.LessThan;
    case Operator.GreaterThanOrEqual:
      return HumanOperator.GreaterThanOrEqual;
    case Operator.LessThanOrEqual:
      return HumanOperator.LessThanOrEqual;
    case Operator.InsideArea:
      return HumanOperator.InsideArea;
    case Operator.OutsideArea:
      return HumanOperator.OutsideArea;
    case Operator.InsideCircle:
      return HumanOperator.InsideCircle;
    case Operator.OutsideCircle:
      return HumanOperator.OutsideCircle;
    case Operator.Boolean:
      return HumanOperator.Boolean;
    default:
      throw new Error(`Invalid Operator ${operator}`);
  }
};

const stringOperators: HumanOperator[] = [
  HumanOperator.Equal,
  HumanOperator.NotEqual,
];

export const stringAndContainsOperators: HumanOperator[] = [
  HumanOperator.Equal,
  HumanOperator.NotEqual,
  HumanOperator.Contains,
  HumanOperator.NotContains,
];

export const numberOperators: HumanOperator[] = [
  HumanOperator.Equal,
  HumanOperator.NotEqual,
  HumanOperator.GreaterThan,
  HumanOperator.LessThan,
  HumanOperator.GreaterThanOrEqual,
  HumanOperator.LessThanOrEqual,
];

const stringOperatorsWildcards: HumanOperator[] = [
  HumanOperator.Equal,
  HumanOperator.NotEqual,
  HumanOperator.Contains,
  HumanOperator.NotContains,
  HumanOperator.StartsWith,
  HumanOperator.DoesNotStartWith,
  HumanOperator.EndsWith,
  HumanOperator.DoesNotEndWith,
];
const numberOperatorsWildcards: HumanOperator[] = [
  HumanOperator.Equal,
  HumanOperator.NotEqual,
  HumanOperator.GreaterThan,
  HumanOperator.LessThan,
  HumanOperator.GreaterThanOrEqual,
  HumanOperator.LessThanOrEqual,
  HumanOperator.Contains,
  HumanOperator.NotContains,
  HumanOperator.StartsWith,
  HumanOperator.DoesNotStartWith,
  HumanOperator.EndsWith,
  HumanOperator.DoesNotEndWith,
];
const positionInAreaOperators: HumanOperator[] = [
  HumanOperator.InsideArea,
  HumanOperator.OutsideArea,
];
const positionOperators: HumanOperator[] = [
  HumanOperator.InsideCircle,
  HumanOperator.OutsideCircle,
];
const booleanOperatorsWildcards: HumanOperator[] = [HumanOperator.Boolean];

export const MappedShipDbToHumanOperators: Record<string, HumanOperator> = {
  WithIn: HumanOperator.InsideCircle,
  'equal to': HumanOperator.Equal,
  Equal: HumanOperator.Equal,
  NotEqual: HumanOperator.NotEqual,
  'not equal to': HumanOperator.NotEqual,
  GreaterThan: HumanOperator.GreaterThan,
  LessThan: HumanOperator.LessThan,
  'greather than': HumanOperator.GreaterThan,
  'less than': HumanOperator.LessThan,
  contains: HumanOperator.Contains,
  Contains: HumanOperator.Contains,
  'inside polygon': HumanOperator.InsideArea,
};

export const MappedShipDbTypeToFrontendType: Record<
  string,
  'string' | 'number' | 'boolean'
> = {
  str: 'string',
  int: 'number',
  datetime: 'string',
  float: 'number',
  bool: 'boolean',
};

export interface FilterCriteria {
  data_types: Array<'string' | 'number' | 'boolean'>;
  operators: HumanOperator[];
  limit: Limit;
  values: Array<any>;
  icon: IconType;
}

export const filterOptionsTemplate: Record<string, FilterCriteria> = {
  BS_COUNTRY: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: ['USA', 'CAN', 'UK'],
    icon: PiFlagThin,
  },
  BS_MMSI: {
    data_types: ['number'],
    operators: numberOperatorsWildcards,
    limit: { maxChars: 9 },
    values: [],
    icon: PiRadioThin,
  },
  CALLSIGN: {
    data_types: ['string'],
    operators: stringOperatorsWildcards,
    limit: { maxChars: 5 },
    values: [],
    icon: PiIdentificationCardThin,
  },
  CARGOTYPE: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: cargoTypeList,
    icon: PiCodesandboxLogoThin,
  },
  CIRCLE_POSITION: {
    data_types: ['string'],
    operators: positionOperators,
    limit: {},
    values: [],
    icon: PiCircleThin,
  },
  COG: {
    data_types: ['number'],
    operators: numberOperators,
    limit: { range: { min: 0, max: 360 } },
    values: [],
    icon: PiPathThin,
  },
  COUNTRY: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: [],
    icon: PiFlagThin,
  },
  DATA_SOURCE_TYPE: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: dataSourceTypeList,
    icon: CiSatellite1,
  },
  DESTINATION: {
    data_types: ['string'],
    operators: stringOperatorsWildcards,
    limit: {},
    values: [],
    icon: PiMapPinLineThin,
  },
  DRAUGHT: {
    data_types: ['number'],
    operators: numberOperators,
    limit: { range: { min: 0, max: 25.5 } },
    values: [],
    icon: PiAnchorSimpleThin,
  },
  ETA: {
    data_types: ['string'],
    operators: numberOperators,
    limit: { isDate: true },
    values: [],
    icon: PiCalendarThin,
  },
  FILTER: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: [],
    icon: PiFunnelThin,
  },
  HAS_INLAND: {
    data_types: ['boolean'],
    operators: booleanOperatorsWildcards,
    limit: {},
    values: [],
    icon: PiFunnelThin,
  },
  HEADING: {
    data_types: ['number'],
    operators: numberOperators,
    limit: { range: { min: 0, max: 360 } },
    values: [],
    icon: PiCompassThin,
  },
  IMO: {
    data_types: ['number'],
    operators: numberOperatorsWildcards,
    limit: { maxChars: 7 },
    values: [],
    icon: PiHashThin,
  },
  INVALID_IMO: {
    data_types: ['boolean'],
    operators: booleanOperatorsWildcards,
    limit: {},
    values: [],
    icon: PiHashThin,
  },
  VALID_IMO: {
    data_types: ['boolean'],
    operators: booleanOperatorsWildcards,
    limit: {},
    values: [],
    icon: PiHashThin,
  },
  LENGTH: {
    data_types: ['number'],
    operators: numberOperators,
    limit: { range: { min: 0, max: 1022 } },
    values: [],
    icon: PiArrowsLeftRightThin,
  },
  MISSING_AIS_DATA: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: missingAisDataList,
    icon: PiFunnelThin,
  },
  MMSI: {
    data_types: ['number'],
    operators: numberOperatorsWildcards,
    limit: { maxChars: 9 },
    values: [],
    icon: PiRadioThin,
  },
  NAME: {
    data_types: ['string'],
    operators: stringOperatorsWildcards,
    limit: {},
    values: [],
    icon: PiTagThin,
  },
  NAV_STATUS: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: [],
    icon: PiCompassThin,
  },
  POSITION: {
    data_types: ['string'],
    operators: positionInAreaOperators,
    limit: {},
    values: [], // NEEDS TO BE UPDATED DYNAMICALLY
    icon: PiMapPinThin,
  },
  REGION: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: [],
    icon: PiPolygonThin,
  },
  ROT: {
    data_types: ['number'],
    operators: numberOperators,
    limit: { range: { min: -127, max: 127 } },
    values: [],
    icon: PiArrowClockwiseThin,
  },
  SOURCE_TAG: {
    data_types: ['string'],
    operators: stringOperatorsWildcards,
    limit: {},
    values: [],
    icon: CiSatellite1,
  },
  SHIPLIST: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: [], // NEEDS TO BE UPDATED DYNAMICALLY
    icon: PiBoatThin,
  },
  SOG: {
    data_types: ['number'],
    operators: numberOperators,
    limit: { range: { min: 0, max: 102.3 } },
    values: [],
    icon: PiGaugeThin,
  },
  TYPE_OF_MOBILE: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: typeOfMobile,
    icon: PiFunnelThin,
  },
  TYPE_OF_SHIP: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: [],
    icon: PiBoatThin,
  },
  VENDORID: {
    data_types: ['string'],
    operators: stringOperators,
    limit: {},
    values: [],
    icon: PiFunnelThin,
  },
  VESSEL_MMSI: {
    data_types: ['number'],
    operators: numberOperatorsWildcards,
    limit: { maxChars: 9 },
    values: [],
    icon: PiRadioThin,
  },
  WIDTH: {
    data_types: ['number'],
    operators: numberOperators,
    limit: { range: { min: 0, max: 1022 } },
    values: [],
    icon: PiArrowsOutLineHorizontalThin,
  },
};

export const applyFilter = (
  rowValue: any,
  filterValue: any,
  operator: HumanOperator
) => {
  if (typeof rowValue === 'string' && typeof filterValue === 'string') {
    rowValue = rowValue.toLowerCase();
    filterValue = filterValue.toLowerCase();
  }

  switch (operator) {
    case HumanOperator.Equal:
      return rowValue === filterValue;
    case HumanOperator.NotEqual:
      return rowValue !== filterValue;
    case HumanOperator.GreaterThan:
      return rowValue > filterValue;
    case HumanOperator.LessThan:
      return rowValue < filterValue;
    case HumanOperator.GreaterThanOrEqual:
      return rowValue >= filterValue;
    case HumanOperator.LessThanOrEqual:
      return rowValue <= filterValue;
    case HumanOperator.Contains:
      return rowValue.includes(filterValue);
    case HumanOperator.NotContains:
      return !rowValue.includes(filterValue);
    case HumanOperator.StartsWith:
      return rowValue.startsWith(filterValue);
    case HumanOperator.DoesNotStartWith:
      return !rowValue.startsWith(filterValue);
    case HumanOperator.EndsWith:
      return rowValue.endsWith(filterValue);
    case HumanOperator.DoesNotEndWith:
      return !rowValue.endsWith(filterValue);
    default:
      return true; // Default to true if no valid operator is provided
  }
};
